import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { DISCLAIMERS, EXERCISE_DEEP_LINK_PARAM } from '@erectus/shared';
import type { Exercise, ExerciseRegion } from '@erectus/shared';
import { Button, Card, EmptyState, Pill } from '@/ui/primitives.js';
import { BackLink, PageHeader } from '@/ui/Layout.js';
import { useAuth } from '@/stores/auth.js';
import { EXERCISE_LIBRARY, localDayKey, logExerciseDone, type ExerciseLogEntry } from '@/lib/repo.js';
import { useWarm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { track } from '@/lib/analytics.js';
import { ExerciseIllustration } from './ExerciseIllustration.js';
import { REGION_LABEL, POSITION_LABEL } from './tags.js';
import { Icon } from '@/ui/icons.js';

/**
 * Exercise snacks (docs/PRODUCT.md §8): the PT's list of moves, grouped by
 * region. A snack is 2–4 of them, 2–5 minutes; programs put them together.
 *
 * Regions name the body area, never a symptom or condition (LEGAL §0). Every
 * exercise carries its own precautions and the fixed stop-if-it-hurts line.
 */
const REGIONS: Array<{ id: ExerciseRegion | 'all'; label: string }> = [
  { id: 'all', label: 'All' },
  ...(Object.entries(REGION_LABEL) as Array<[ExerciseRegion, string]>).map(([id, label]) => ({ id, label })),
];

export function ExercisesPage(): ReactNode {
  const { user } = useAuth();
  const exercises = EXERCISE_LIBRARY;
  const [tag, setTag] = useState<ExerciseRegion | 'all'>('all');
  const [open, setOpen] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const deepLinked = searchParams.get(EXERCISE_DEEP_LINK_PARAM);
  /**
   * "Done" means done today: read from the exercise log (one entry per
   * exercise per local day), so it survives leaving the screen or reopening
   * the app, and clears on its own the next day. Shares the cache entry the
   * Progress calendar reads, so both agree the moment one is marked.
   */
  const [logState, setLog] = useWarm<ExerciseLogEntry[]>(
    user ? warmKeys.exerciseLog(user.uid) : null,
    () => loaders.exerciseLog(user!.uid),
    'Could not load your exercise log.',
  );
  const log = useMemo(() => (logState.status === 'ready' ? logState.data : []), [logState]);
  const today = localDayKey(Date.now());
  const doneToday = useMemo(
    () => new Set(log.filter((e) => localDayKey(e.doneAt) === today).map((e) => e.exerciseId)),
    [log, today],
  );

  /**
   * Arriving from one of Darwin's recommendations: open that exercise and
   * scroll to it, then drop the parameter so a later reload does not keep
   * yanking the page back to it. The replace keeps the link's state (where
   * the back link returns to).
   */
  useEffect(() => {
    if (!deepLinked) return;
    if (!exercises.some((e) => e.id === deepLinked)) return;
    setTag('all');
    setOpen(deepLinked);
    const node = cardRefs.current.get(deepLinked);
    node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const next = new URLSearchParams(searchParams);
    next.delete(EXERCISE_DEEP_LINK_PARAM);
    setSearchParams(next, { replace: true, state });
  }, [deepLinked, exercises, searchParams, setSearchParams, state]);

  const filtered = useMemo(
    () => exercises.filter((e) => tag === 'all' || e.region === tag),
    [exercises, tag],
  );

  const complete = async (exercise: Exercise): Promise<void> => {
    if (!user || doneToday.has(exercise.id)) return;
    track('exercise_complete', { exercise_id: exercise.id });
    // Shown at once (here and on Progress's calendar), then written.
    setLog([{ exerciseId: exercise.id, doneAt: Date.now() }, ...log]);
    const saved = await logExerciseDone(user.uid, exercise.id).catch(() => null);
    if (!saved) setLog(log); // the write failed: take the mark back
  };

  return (
    <div className="space-y-4">
      <BackLink home="plan" />
      <PageHeader
        title="Exercise snacks"
        subtitle="Short moves for desk days. Two to four of them make a 2–5 minute snack."
      />

      <div className="flex flex-wrap gap-2">
        {REGIONS.map((t) => (
          <Pill key={t.id} active={tag === t.id} onClick={() => setTag(t.id)}>
            {t.label}
          </Pill>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No exercises loaded"
          message="Run the seed script (pnpm seed:exercises) against your Firebase project."
        />
      ) : null}

      {filtered.map((exercise) => {
        const expanded = open === exercise.id;
        return (
          <Card
            key={exercise.id}
            ref={(node) => {
              if (node) cardRefs.current.set(exercise.id, node);
              else cardRefs.current.delete(exercise.id);
            }}
            className={`space-y-2 ${
              expanded && deepLinked === exercise.id ? 'ring-2 ring-ink' : ''
            }`}
          >
            <button
              className="flex w-full items-start gap-3 text-left"
              onClick={() => {
                setOpen(expanded ? null : exercise.id);
                if (!expanded) track('exercise_view', { exercise_id: exercise.id });
              }}
              aria-expanded={expanded}
            >
              <ExerciseIllustration id={exercise.id} fallback={exercise.illustration} />
              <span className="flex-1">
                <span className="block font-semibold">{exercise.name}</span>
                <span className="block text-xs text-muted">
                  {exercise.reps} · {exercise.position ? `${POSITION_LABEL[exercise.position]} · ` : ''}level{' '}
                  {exercise.difficulty}
                </span>
              </span>
              {doneToday.has(exercise.id) ? (
                <span className="flex items-center gap-1 text-xs font-medium text-[color:var(--color-state-good)]">
                  <Icon name="check" size={16} strokeWidth={2.4} />
                  Today
                </span>
              ) : null}
              <Icon name={expanded ? 'minus' : 'plus'} size={18} className="text-muted" />
            </button>

            {expanded ? (
              <div className="space-y-3">
                {exercise.media && exercise.media.length > 0 ? (
                  <div className="-mx-1 flex snap-x gap-2 overflow-x-auto px-1">
                    {exercise.media.map((m) =>
                      m.kind === 'image' ? (
                        <img key={m.src} src={m.src} alt={m.alt} className="h-48 snap-start rounded-xl object-cover" loading="lazy" />
                      ) : (
                        <video key={m.src} src={m.src} aria-label={m.alt} className="h-48 snap-start rounded-xl" controls playsInline muted />
                      ),
                    )}
                  </div>
                ) : (
                  <div className="flex justify-center rounded-xl bg-surface py-2">
                    <ExerciseIllustration id={exercise.id} fallback={exercise.illustration} large />
                  </div>
                )}
                <Section title="How to do it">
                  <ol className="list-decimal space-y-1 pl-5">
                    {(exercise.steps ?? exercise.cues).map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </Section>
                {exercise.steps ? (
                  <Section title="Keep in mind">
                    <ul className="list-disc space-y-1 pl-5">
                      {exercise.cues.map((cue, i) => (
                        <li key={i}>{cue}</li>
                      ))}
                    </ul>
                  </Section>
                ) : null}
                {exercise.mistakes && exercise.mistakes.length > 0 ? (
                  <Section title="Watch for">
                    <ul className="list-disc space-y-1 pl-5">
                      {exercise.mistakes.map((m, i) => (
                        <li key={i}>{m}</li>
                      ))}
                    </ul>
                  </Section>
                ) : null}
                <div className="rounded-xl border border-[color:var(--color-state-fhp)]/40 bg-surface p-3 text-sm">
                  <p className="font-semibold">Take care</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-ink-soft">
                    {(exercise.precautions ?? []).map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                    <li>{DISCLAIMERS.exercise}</li>
                  </ul>
                </div>
                <Button
                  variant={doneToday.has(exercise.id) ? 'secondary' : 'primary'}
                  disabled={doneToday.has(exercise.id)}
                  onClick={() => void complete(exercise)}
                >
                  {doneToday.has(exercise.id) ? 'Done today' : 'Mark as done'}
                </Button>
              </div>
            ) : null}
          </Card>
        );
      })}

    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <div className="text-sm text-ink-soft">
      <p className="mb-1 font-semibold text-ink">{title}</p>
      {children}
    </div>
  );
}
